import { type ReactNode, useMemo } from "react";
import type {
	ColDef,
	ICellRendererParams,
	ValueFormatterParams,
} from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import {
	Autocomplete,
	Box,
	IconButton,
	MenuItem,
	Select,
	TextField,
	Typography,
	type SelectChangeEvent,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { globalDefaultColDef } from "@/setup/ag-grid/mesf-theme";
import {
	HEADER_PAD_CLASS,
	NUMBER_CELL_CLASS,
	ONE_LINE_CELL_CLASS,
	OneLineCellStyles,
} from "@/controls/ag-grid/OneLineCells";
import type { BarrelLayout, PassId } from "../models/Barrel";

interface PositionCellRendererParams extends ICellRendererParams<BarrelLayout> {
	maxPosition: number;
	isEditing: boolean;
	onReorder?: (layoutId: number, newPosition: number) => void;
}

/** Value and its arrow as one block in the middle of the cell, the way the old Select sat. */
const CenteredCell = ({ children }: { children: ReactNode }) => (
	<Box
		sx={{
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			width: "100%",
			height: "100%",
		}}
	>
		{children}
	</Box>
);

const PositionCellRenderer = (params: PositionCellRendererParams) => {
	const { data, maxPosition, isEditing, onReorder } = params;

	if (!data) return null;

	if (!isEditing || maxPosition <= 1) {
		return <CenteredCell>{data.Position}</CenteredCell>;
	}

	const handleChange = (event: SelectChangeEvent<number>) => {
		const newPosition = event.target.value as number;
		if (newPosition !== data.Position && onReorder) {
			onReorder(data.Id, newPosition);
		}
	};

	return (
		<CenteredCell>
			<Select
				value={data.Position}
				onChange={handleChange}
				size="small"
				variant="standard"
				disableUnderline
				sx={{
					fontSize: "inherit",
					fontVariantNumeric: "tabular-nums",
					// The arrow takes 24 px on the right; the same on the left keeps the number
					// itself under the "#".
					"& .MuiSelect-select": { py: 0, pl: "24px" },
				}}
			>
				{Array.from({ length: maxPosition }, (_, i) => i + 1).map((num) => (
					<MenuItem key={num} value={num}>
						{num}
					</MenuItem>
				))}
			</Select>
		</CenteredCell>
	);
};

interface PassIdCellRendererParams extends ICellRendererParams<BarrelLayout> {
	passIds: PassId[];
	isEditing: boolean;
	onUpdate?: (layoutId: number, newPassId: number) => void;
}

/** Room MUI keeps on the right of an Autocomplete for its popup arrow. */
const ARROW_ROOM = 30;

/** Up to 61 passes per stand, so the picker is typed into rather than scrolled. */
const PassIdCellRenderer = (params: PassIdCellRendererParams) => {
	const { data, passIds, isEditing, onUpdate } = params;

	if (!data) return null;

	if (!isEditing || passIds.length === 0) {
		return <CenteredCell>{data.PassName}</CenteredCell>;
	}

	const selected = passIds.find((pass) => pass.Id === data.PassId) ?? null;
	const label = selected?.Name ?? data.PassName ?? "";

	return (
		<CenteredCell>
			{/* The hidden copy of the name sizes the picker, so the arrow sits right after the
			    name; the arrow's room is mirrored on the left to keep the name centred. */}
			<Box sx={{ display: "inline-grid", maxWidth: "100%" }}>
				<Box
					component="span"
					aria-hidden
					sx={{
						gridArea: "1 / 1",
						visibility: "hidden",
						whiteSpace: "pre",
						height: 0,
						overflow: "hidden",
						pl: `${ARROW_ROOM + 4}px`,
						pr: `${ARROW_ROOM + 10}px`,
					}}
				>
					{label}
				</Box>
				<Autocomplete
					options={passIds}
					value={selected}
					size="small"
					sx={{
						gridArea: "1 / 1",
						minWidth: 0,
						"& .MuiInputBase-root": { pl: `${ARROW_ROOM + 4}px` },
					}}
					disableClearable={selected !== null}
					autoHighlight
					getOptionLabel={(option) => option.Name}
					isOptionEqualToValue={(option, current) => option.Id === current.Id}
					onChange={(_event, option) => {
						if (option && option.Id !== data.PassId && onUpdate) {
							onUpdate(data.Id, option.Id);
						}
					}}
					renderOption={(props, option) => {
						const { key, ...rest } = props as typeof props & { key: string };
						return (
							<li key={option.Id} {...rest}>
								{option.Name}
							</li>
						);
					}}
					slotProps={{ popper: { sx: { minWidth: 240 } } }}
					renderInput={(inputParams) => (
						<TextField
							{...inputParams}
							variant="standard"
							title={data.PassName}
							slotProps={{
								input: {
									...inputParams.InputProps,
									disableUnderline: true,
									sx: { fontSize: "inherit" },
								},
							}}
						/>
					)}
				/>
			</Box>
		</CenteredCell>
	);
};

interface ActionsCellRendererParams extends ICellRendererParams<BarrelLayout> {
	isEditing: boolean;
	onDelete?: (layout: BarrelLayout) => void;
}

const ActionsCellRenderer = (params: ActionsCellRendererParams) => {
	const { data, isEditing, onDelete } = params;

	if (!isEditing || !data) return null;

	return (
		<Box sx={{ display: "flex", justifyContent: "center" }}>
			<IconButton
				size="small"
				onClick={() => onDelete?.(data)}
				title="Remove pass"
				sx={{ p: 0.25, color: "error.main" }}
			>
				<DeleteIcon fontSize="small" />
			</IconButton>
		</Box>
	);
};

interface BarrelLayoutsTableProps {
	layouts: BarrelLayout[];
	passIds?: PassId[];
	isEditing?: boolean;
	onUpdate?: (layoutId: number, newPassId: number) => void;
	onReorder?: (layoutId: number, newPosition: number) => void;
	onDelete?: (layout: BarrelLayout) => void;
}

export const BarrelLayoutsTable = ({
	layouts,
	passIds = [],
	isEditing = false,
	onUpdate,
	onReorder,
	onDelete,
}: BarrelLayoutsTableProps) => {
	const maxPosition = layouts.length;

	const columnDefs: ColDef<BarrelLayout>[] = useMemo(
		() => [
			{
				field: "Position",
				headerName: "#",
				width: 70,
				sort: "asc",
				cellRenderer: PositionCellRenderer,
				cellRendererParams: { maxPosition, isEditing, onReorder },
			},
			{
				field: "PassName",
				headerName: "Pass Id",
				flex: 1,
				minWidth: 140,
				cellClass: ONE_LINE_CELL_CLASS,
				cellRenderer: PassIdCellRenderer,
				cellRendererParams: { passIds, isEditing, onUpdate },
			},
			{
				field: "PassScheduledTonnage",
				headerName: "Sched Tons",
				width: 120,
				cellClass: [ONE_LINE_CELL_CLASS, NUMBER_CELL_CLASS],
				valueFormatter: (params: ValueFormatterParams<BarrelLayout>) =>
					params.value != null
						? params.value.toLocaleString("en-US", {
								minimumFractionDigits: 2,
								maximumFractionDigits: 2,
							})
						: "",
			},
			...(isEditing
				? [
						{
							headerName: "",
							width: 60,
							cellRenderer: ActionsCellRenderer,
							cellRendererParams: { isEditing, onDelete },
							sortable: false,
							filter: false,
							suppressSizeToFit: true,
						} as ColDef<BarrelLayout>,
					]
				: []),
		],
		[isEditing, passIds, maxPosition, onUpdate, onReorder, onDelete],
	);

	// One line per pass, so the rows keep their height and the grid fills the tab.
	const defaultColDef = useMemo(
		() => ({
			...globalDefaultColDef,
			sortable: false,
			filter: false,
			wrapText: false,
			autoHeight: false,
			headerClass: HEADER_PAD_CLASS,
		}),
		[],
	);

	if (layouts.length === 0) {
		return (
			<Box
				sx={{
					flex: 1,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
					color: "text.secondary",
				}}
			>
				<Typography variant="body2">No passes assigned</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ flex: 1, minHeight: 0 }}>
			<OneLineCellStyles />
			<AgGridReact<BarrelLayout>
				rowData={layouts}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				rowHeight={28}
				headerHeight={28}
				suppressCellFocus
				suppressContextMenu
				suppressColumnVirtualisation
				animateRows={false}
			/>
		</Box>
	);
};
